import { Suspense, useState } from 'react'

import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import { Select } from 'src/components/Select/Select'
import { useAccountInfo } from 'src/features/misc/hooks/useAccountInfo'
import { GetTrialHospitalsByOwnPermissionDocument } from 'src/lib/gql-client'
import { useGqlQuerySWR } from 'src/lib/gql-client/request'

import { HospitalInformationWidget } from '../HospitalInformationWidget/HospitalInformationWidget'
import { HospitalSharedFileWidget } from '../HospitalSharedFileWidget/HospitalSharedFileWidget'
import { LoadingWidget } from '../LoadingWidget/LoadingWidget'

export const TrialTop: React.FC = () => {
  const { trialName, role } = useAccountInfo()

  const {
    data: { trialHospitals: trialHospitals },
  } = useGqlQuerySWR(
    GetTrialHospitalsByOwnPermissionDocument,
    {},
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  )

  const mainTrialHospitals = trialHospitals.filter(
    hospital => hospital.role === 'Main',
  )
  const partnerHospitals = trialHospitals.filter(
    hospital => hospital.role === 'Partner',
  )

  const selectableHospitals = mainTrialHospitals.map(hospital => ({
    value: hospital.uid,
    label: (
      <Text
        as="span"
        display="block"
        w="full"
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
      >
        {hospital.hospital.name}
      </Text>
    ),
  }))

  const [selectedHospitalUid, setSelectedHospitalUid] = useState<string>(
    selectableHospitals[0].value,
  )

  const selectedMainTrialHospital = mainTrialHospitals.find(
    hospital => hospital.uid === selectedHospitalUid,
  )

  if (!selectedMainTrialHospital) {
    return null
  }

  const selectedPartnerHospitals = partnerHospitals.filter(
    hospital => hospital.mainTrialHospital?.uid === selectedHospitalUid,
  )

  return (
    <Stack as="main" padding="6" spacing={4}>
      <Flex gap={4} justifyContent="space-between" alignItems="center">
        <Text
          as="h1"
          fontSize="xl"
          fontWeight="bold"
          overflow="hidden"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
        >
          {trialName}
        </Text>

        {role === 'DM' && (
          <Flex justifyContent="space-between" alignItems="center" gap={2}>
            <Text
              fontSize="sm"
              fontWeight="bold"
              color="gray.600"
              whiteSpace="nowrap"
            >
              実施医療機関
            </Text>
            <Box w="260px">
              <Select
                items={selectableHospitals}
                value={selectedHospitalUid}
                onChange={value => setSelectedHospitalUid(value)}
              />
            </Box>
          </Flex>
        )}
      </Flex>

      <Box w="560px">
        <HospitalInformationWidget
          mainTrialHospital={selectedMainTrialHospital}
          partnerHospitals={selectedPartnerHospitals}
        />
      </Box>

      <Box minW="560px" w="full">
        <Suspense fallback={<LoadingWidget />}>
          <HospitalSharedFileWidget
            selectedTrialHospitalUidWithDM={
              role === 'DM' ? selectedHospitalUid : undefined
            }
          />
        </Suspense>
      </Box>
    </Stack>
  )
}
